import { useEffect, useState } from "react";
import { Button, Card, Col, Descriptions, Form, Input, message, Row, Space, Tag, Typography } from "antd";
import { KeyOutlined, LockOutlined, ReloadOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";
import { api } from "../services/apiClient";

const { Title, Text } = Typography;

const roleLabels = {
  SUPER_ADMIN: "SUPER ADMIN",
  ADMIN: "ADMIN",
  USER: "USER",
};

function formatDateTime(value) {
  const date = moment(value);
  return value && date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
}

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [form] = Form.useForm();

  const loadProfile = async () => {
    setLoading(true);
    try {
      setProfile(await api.get("/api/auth/me"));
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const changePassword = async (values) => {
    setChangingPassword(true);
    try {
      await api.post("/api/auth/change-password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success("Đã đổi mật khẩu");
      form.resetFields();
    } catch (error) {
      message.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const role = profile?.role || "USER";
  const isActive = profile?.status === "ACTIVE";

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Thông tin tài khoản</Title>
          <Text type="secondary">Quản lý thông tin đăng nhập và bảo mật tài khoản.</Text>
        </div>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={loadProfile}>Làm mới</Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card className="criclebox h-full" title={<Space><UserOutlined />Thông tin cơ bản</Space>} loading={loading}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Username">{profile?.username || "-"}</Descriptions.Item>
              <Descriptions.Item label="Email">{profile?.email || "-"}</Descriptions.Item>
              <Descriptions.Item label="Vai trò"><Tag color={role === "SUPER_ADMIN" ? "purple" : role === "ADMIN" ? "blue" : "default"}>{roleLabels[role] || role}</Tag></Descriptions.Item>
              <Descriptions.Item label="Trạng thái"><Tag color={isActive ? "green" : "red"}>{isActive ? "Hoạt động" : "Không hoạt động"}</Tag></Descriptions.Item>
              <Descriptions.Item label="Ngày tạo">{formatDateTime(profile?.createdAt)}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="criclebox h-full" title={<Space><KeyOutlined />Đổi mật khẩu</Space>}>
            <Form form={form} layout="vertical" onFinish={changePassword} autoComplete="off">
              <Form.Item name="currentPassword" label="Mật khẩu hiện tại" rules={[{ required: true, message: "Vui lòng nhập mật khẩu hiện tại" }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Nhập mật khẩu hiện tại" />
              </Form.Item>
              <Form.Item name="newPassword" label="Mật khẩu mới" rules={[{ required: true, min: 6, message: "Mật khẩu mới phải có ít nhất 6 ký tự" }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Tối thiểu 6 ký tự" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Xác nhận mật khẩu mới"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Vui lòng xác nhận mật khẩu mới" },
                  ({ getFieldValue }) => ({ validator: (_, value) => !value || getFieldValue("newPassword") === value ? Promise.resolve() : Promise.reject(new Error("Mật khẩu xác nhận chưa khớp")) }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Nhập lại mật khẩu mới" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={changingPassword} icon={<KeyOutlined />}>Đổi mật khẩu</Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
